import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, TemplateRef, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { takeUntil } from 'rxjs';

import { ButtonComponent } from '@shared/components/button';
import { BreakpointService } from '@shared/services/breakpoint';

import { ArrowPosition } from './tooltip-data.interface';
import { TooltipRef } from './tooltip-ref';
import { TOOLTIP_DATA } from './tooltip.tokens';

const BORDER_RADIUS_PADDING = 4;
const HALF_OF_ARROW_WIDTH = 6;

@Component({
  selector: 'app-tooltip',
  templateUrl: './tooltip.component.html',
  styleUrls: ['./tooltip.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, NgTemplateOutlet, ButtonComponent, TranslatePipe],
})
export class TooltipComponent {
  public readonly data = inject(TOOLTIP_DATA);
  readonly #tooltipRef = inject(TooltipRef);
  readonly #breakpointService = inject(BreakpointService);
  readonly #elementRef = inject(ElementRef<HTMLElement>);

  public readonly isMobile = this.#breakpointService.isMobile;
  public readonly arrow = signal<
    | {
        position: ArrowPosition | undefined;
        offsetX: number;
      }
    | undefined
  >(undefined);

  constructor() {
    this.watchArrowPositionChanges();

    // don't remove this line, hack to show arrow when tooltip is opened 🤷‍♂️
    effect(() => this.arrow());
  }

  public readonly arrowCssClass = computed(() => {
    const arrow = this.arrow();
    if (!arrow) {
      return '';
    }

    const arrowPosition = arrow.position;
    if (arrowPosition === null || arrowPosition === undefined) {
      return '';
    }

    return arrowPosition.position;
  });

  public readonly classes = computed(() => {
    const arrowCssClass = this.arrowCssClass();
    if (!this.data.styleClasses) return arrowCssClass;

    return `${arrowCssClass} ${this.data.styleClasses}`;
  });

  public readonly translateX = computed(() => {
    const arrow = this.arrow();
    if (!arrow) return '';
    return `${arrow.offsetX}px 0`;
  });

  public isTemplate(tooltip: string | TemplateRef<unknown>): tooltip is TemplateRef<unknown> {
    return tooltip instanceof TemplateRef;
  }

  public closeTooltip(): void {
    this.#tooltipRef.close();
  }

  private watchArrowPositionChanges() {
    this.data.arrowPosition$.pipe(takeUntilDestroyed(), takeUntil(this.#tooltipRef.afterClosed$())).subscribe((arrowPosition) => {
      if (arrowPosition && arrowPosition.hasOwnProperty('offsetX')) {
        const offsetX = (arrowPosition as { offsetX: number }).offsetX;
        const tooltipWidth = this.#elementRef.nativeElement.getBoundingClientRect().width;

        this.arrow.set({
          position: arrowPosition,
          offsetX: Math.min(Math.max(offsetX - HALF_OF_ARROW_WIDTH, BORDER_RADIUS_PADDING), tooltipWidth - BORDER_RADIUS_PADDING),
        });
      } else {
        this.arrow.set({
          position: arrowPosition,
          offsetX: BORDER_RADIUS_PADDING,
        });
      }
    });
  }
}
